// @flow

import appConfig from '../config';
import WizzeeLog from '../lib/wizzeeLog';

type CachedPromises = {
  [url: string]: CachedPromise,
};

type CachedPromise = {|
  promise: Promise<*>,
  expirationDate: number,
|};

type ExpirationTimeConfig = { url?: string, regex?: RegExp, time: ?number };

let cachedPromises: CachedPromises = {};

const expirationTimeConfigsInSeconds: ExpirationTimeConfig[] = [
  {
    url: 'productOffering',
    time: 3600,
  },
  {
    url: 'resourceSpecification',
    time: 3600,
  },
  {
    url: 'resourceSpecification/name/msisdn',
    time: null,
  },
  {
    regex: new RegExp(/resource\/resourceSpecificationOuid\/\d+/),
    time: null,
  },
  {
    url: 'private/productOffering',
    time: 300,
  },
  {
    url: 'private/login',
    time: 300,
  },
  {
    url: 'private/customer/connected',
    time: null,
  },
  {
    url: 'private/resource',
    time: 300,
  },
  {
    url: 'private/individual',
    time: 300,
  },
  {
    url: 'private/billingAccount',
    time: 300,
  },
  {
    url: 'private/billingAccount/accountWallet',
    time: null,
  },
  {
    url: 'private/billing/accountWallet',
    time: null,
  },
  {
    url: 'private/product',
    time: 300,
  },
  {
    url: 'private/productOrder',
    time: 300,
  },
  {
    url: 'private/resourceDelivery',
    time: 300,
  },
  {
    url: 'private/sandBox',
    time: 300,
  },
  {
    url: 'private/appliedBillingCharge',
    time: 300,
  },
  {
    url: 'private/paymentRequest',
    time: 300,
  },
  {
    url: 'private/billingAccountBalance/status/PAID',
    time: 300,
  },
  {
    url: 'private/notification/media/TIMELINE',
    time: 10,
  },
  {
    url: 'private/notification/media/WEB',
    time: 10,
  },
  {
    url: 'private/customer/paymentMean/type/BRAINTREE/init',
    time: null,
  },
  {
    regex: new RegExp(/private\/networkBucket\/\d+/),
    time: 5,
  },
];

export const DASHBOARD_CACHE_DURATION = 300;

const getExpirationTime = (url: string) => {
  const urlKey = url.replace(appConfig.API_URL, '').replace(/\/(\w+\/)*v\d*\//, '$1');
  const customExpirationTimeConfig = expirationTimeConfigsInSeconds.find(({ url: cacheUrl, regex }) => {
    if (cacheUrl) {
      return urlKey === cacheUrl;
    } else if (regex) {
      return regex.test(urlKey);
    } else {
      return false;
    }
  });

  if (customExpirationTimeConfig?.time === undefined) {
    WizzeeLog.info('No custom expiration time set for ', urlKey);
  }

  return (customExpirationTimeConfig?.time || 0.5) * 1000;
};

const isMethodCached = method => !['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

const invalidate = () => {
  cachedPromises = {};
};

const cache = (promise: Promise<*>, url: string, options: ?Object) => {
  if (options && (!isMethodCached(options.method) || options.noCache)) {
    // if we have a method that modifies things, we invalidate the cache so that further requests take in to account the changes
    invalidate();
    return;
  }

  const expirationDate = Date.now() + getExpirationTime(url);
  cachedPromises[url] = {
    promise: promise.catch(() => {
      delete cachedPromises[url];
    }),
    expirationDate,
  };
};

const get = (url: string, options: ?Object) => {
  if (options && (!isMethodCached(options.method) || options.noCache)) {
    return null;
  }

  const cachedPromise = cachedPromises[url];
  if (!cachedPromise || cachedPromise.expirationDate <= Date.now()) {
    return null;
  }

  return cachedPromise.promise;
};

export default {
  invalidate,
  cache,
  get,
};
